import { useState } from 'react'

import { IconBolt } from '@tabler/icons-react'

import { BlockBase, PlatformAction, CaptureBlock } from '../types/blueprint'

import { CaptureBlockActionButton } from './CaptureBlock.style'
import { getPlatformActionsId } from './utils/mapValuesToTemplate'

import { Form } from '@/components/shared-components/BUI/Form'
import { useProject } from '@/context/projectContext'
import { useTemplateRenderer } from '@/context/templateRendererContext'
import {
  Field,
  FormFieldValues,
  useCreateAction,
} from '@/modules/actions/actionHooks'
import { getNewInitialValues } from '@/modules/actions/actionsHelper'
import { CreateActionDrawer } from '@/modules/actions/CreateActionDrawer'

export const ALLOW_ORPHANED_ACTIONS = 'ALLOW_ORPHANED_ACTIONS' // I THINK THIS COULD BE ENABLED FOR ALL TEMPLATES NOW BUT WE DID THIS IN A HURRY AND ONLY ENABLED IT FOR TOOLBOXES. BUT I'M PRETTY CONFIDENT THAT WE CAN CREATE PLATFORM ACTIONS WITHOUT A TEMPLATE RESPONSE ID BECAUSE THE API IS NOW HOOKING UP ORPHANED ACTIONS ON TEMPLATE RESPONSE SAVE

type InlineProps = {
  blockId: string
  value?: PlatformAction[]
  onChange?: (attachments: PlatformAction[]) => void
  block?: CaptureBlock
}

const CreateActionInline: React.FC<InlineProps> = ({
  blockId,
  value,
  onChange,
  block,
}) => {
  const { project } = useProject() || {}
  const {
    templateResponseId,
    config,
    extraInfo,
    renderOptions,
    builderId,
    templateResponseData,
  } = useTemplateRenderer()

  const [editing, setEditing] = useState(false)

  const resourceLinkId = extraInfo?.resourceLinkId as string
  const locationId = extraInfo?.locationId as string

  const { createAction, loading } = useCreateAction(
    templateResponseId,
    blockId,
    resourceLinkId
  )

  // only show actions on templates that have been created unless explicitly allowed
  if (
    !extraInfo[ALLOW_ORPHANED_ACTIONS] &&
    !renderOptions.isPreview &&
    !templateResponseId
  ) {
    return null
  }

  const startEditing = () => setEditing(true)
  const stopEditing = () => setEditing(false)

  const handleSubmit = async (values: FormFieldValues) => {
    const platformAction = await createAction(values)

    onChange([
      ...value,
      {
        id: platformAction.id,
        status: values.status,
        title: values.title,
        description: values.description,
        attachments: platformAction.attachments?.map((a) => ({
          id: a.id,
          url: a.url,
        })),
      },
    ])

    if (!values[Field.Continue]) {
      stopEditing()
    }
  }

  return (
    <>
      <CaptureBlockActionButton
        icon={<IconBolt size={12} />}
        onClick={startEditing}
      >
        Action
      </CaptureBlockActionButton>

      <CreateActionDrawer
        open={editing}
        onClose={stopEditing}
        builderId={builderId}
        isPublic={config?.isPublic}
        loading={loading}
        formProps={{
          initialValues: getNewInitialValues(project, locationId),
          onFinish: handleSubmit,
        }}
        contextualDate={config.contextualDate}
        templateBlockData={block}
        templateResponseData={templateResponseData}
        templateResponseId={templateResponseId}
      />
    </>
  )
}

type Props = {
  parentId: string
  actions: BlockBase['platformActions']
  block?: CaptureBlock
}

export const CapturePlatformActions: React.FC<Props> = ({
  parentId,
  actions,
  block,
}) => {
  if (actions === undefined || actions === false) return null

  const platformActionsId = getPlatformActionsId(parentId)
  const initialValue = Array.isArray(actions) ? actions : []

  return (
    <Form.Item name={platformActionsId} initialValue={initialValue} noStyle>
      <CreateActionInline blockId={parentId} block={block} />
    </Form.Item>
  )
}
